import { useState } from "react";
import type { GameState } from "@/game/types";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePublishScore } from "@/hooks/usePublishScore";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { LoginArea } from "@/components/auth/LoginArea";
import { Check, Loader2, Trophy } from "lucide-react";

export function PublishRun({ state }: { state: GameState }) {
  const { user } = useCurrentUser();
  const { mutateAsync: publishScore, isPending } = usePublishScore();
  const { toast } = useToast();
  const [alsoPostNote, setAlsoPostNote] = useState(true);
  const [published, setPublished] = useState(false);

  const handlePublish = async () => {
    try {
      await publishScore({ state, alsoPostNote });
      setPublished(true);
      toast({
        title: "Score submitted! 🏆",
        description: alsoPostNote
          ? "Your run is on the leaderboard and posted to your feed."
          : "Your run is on the global leaderboard.",
      });
    } catch (e) {
      toast({
        title: "Couldn't submit score",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (published) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-primary bg-primary/10 px-4 py-3 font-terminal text-lg text-primary">
        <Check className="size-5" />
        Score submitted to the global leaderboard!
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-3 rounded-lg border bg-card p-4 text-center">
        <div className="flex items-center justify-center gap-2 font-pixel text-[10px] text-primary">
          <Trophy className="size-4" />
          Post your score to the global leaderboard
        </div>
        <p className="font-terminal text-base text-muted-foreground">
          Log in with Nostr to submit your run to the decentralized hall of fame.
        </p>
        <div className="flex justify-center">
          <LoginArea className="max-w-60" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-center gap-2 font-pixel text-[10px] text-primary">
        <Trophy className="size-4" />
        Submit to the global leaderboard
      </div>
      <div className="flex items-center justify-center gap-2">
        <Checkbox
          id="post-note"
          checked={alsoPostNote}
          onCheckedChange={(v) => setAlsoPostNote(v === true)}
        />
        <Label htmlFor="post-note" className="font-terminal text-base">
          Also brag about it in a public note
        </Label>
      </div>
      <Button
        className="w-full font-pixel text-xs"
        disabled={isPending}
        onClick={handlePublish}
      >
        {isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" /> Submitting…
          </>
        ) : (
          "Post My Score ⚡"
        )}
      </Button>
    </div>
  );
}
